import Fuse from "fuse.js";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import * as R from "remeda";
import { useDebouncedCallback } from "./hooks/useDebouncedCallback";
import type { MappedTreatment, MappedTreatmentData } from "./types";

type IcbtSelectorContextType = {
  selectedProgramId?: string;
  setSelectedProgramId: (id: string | undefined) => void;
  selectedTreatmentId?: string;
  setSelectedTreatmentId: (id: string | undefined) => void;
  programsToAssign: Record<string, string[]>;
  filteredTreatments: MappedTreatment[];
  setSearchString: (value: string) => void;
};

const IcbtSelectorContext = createContext<IcbtSelectorContextType | null>(null);

export type IcbtSelectorProviderProps = {
  children: ReactNode;
  selectedTreatmentId?: string;
  selectedProgramId?: string;
  programsToAssign?: Record<string, string[]>;
  treatments: MappedTreatmentData;
};

export const IcbtSelectorProvider = ({
  children,
  selectedTreatmentId,
  selectedProgramId: initialSelectedProgramId,
  programsToAssign = {},
  treatments,
}: IcbtSelectorProviderProps) => {
  const [selectedProgramId, setSelectedProgramId] = useState<
    string | undefined
  >(initialSelectedProgramId);
  const [internalSelectedTreatmentId, setSelectedTreatmentId] = useState<
    string | undefined
  >(selectedTreatmentId);
  const [searchString, setSearchString] = useState("");
  const [filteredTreatments, setFilteredTreatments] = useState(
    treatments.sorted,
  );

  const searchTree = useMemo(
    () =>
      new Fuse<MappedTreatment>(treatments.sorted, {
        includeScore: true,
        isCaseSensitive: false,
        threshold: 0.33,
        keys: [
          { name: "title", weight: 0.8 },
          { name: "programs.title", weight: 0.2 },
        ],
      }),
    [treatments.sorted],
  );

  const searchResults = useMemo(
    () => (searchString ? searchTree.search(searchString) : []),
    [searchString, searchTree],
  );

  useDebouncedCallback(
    () => {
      if (!searchString) {
        setFilteredTreatments(treatments.sorted);
        return;
      }

      type Scored = MappedTreatment & { matchScore?: number };

      const withScores: Scored[] = treatments.sorted.map((treatment) => ({
        ...treatment,
        matchScore: searchResults.find(
          (result) => result.item.id === treatment.id,
        )?.score,
      }));

      const matched = R.intersectionWith(
        withScores,
        searchResults,
        (a, b) => a.id === b.item.id,
      );

      const withCurrent: Scored[] = internalSelectedTreatmentId
        ? R.concat(matched, [
            {
              ...treatments.sorted.find(
                (treatment) => treatment.id === internalSelectedTreatmentId,
              )!,
              matchScore: 0,
            },
          ])
        : matched;

      setFilteredTreatments(
        R.pipe(
          withCurrent,
          R.uniqueBy((r) => r.id),
          R.sortBy((item) => item.matchScore ?? 0),
        ),
      );
    },
    [searchResults, treatments, internalSelectedTreatmentId],
    150,
  );

  return (
    <IcbtSelectorContext.Provider
      value={{
        selectedProgramId,
        setSelectedProgramId,
        selectedTreatmentId: internalSelectedTreatmentId,
        setSelectedTreatmentId,
        programsToAssign,
        filteredTreatments,
        setSearchString,
      }}
    >
      {children}
    </IcbtSelectorContext.Provider>
  );
};

export const useIcbtSelector = () => {
  const context = useContext(IcbtSelectorContext);
  if (!context) {
    throw new Error("useIcbtSelector must be used within IcbtSelectorProvider");
  }
  return context;
};
